'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { googleLogout } from '@react-oauth/google';
import { trackVisitor } from '../utils/luckyOrange';
import { setAuthErrorHandler } from '../services/api';

const UserContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  // Clear user information
  const clearUser = () => {
    setUser(null);
    localStorage.removeItem('user');
    // If Google login, call Google logout method
    if (user && user.provider === 'google') {
      googleLogout();
    }
  };

  // Show login modal
  const openLogin = () => {
    setShowLogin(true);
  };

  // Set up global auth error handler
  useEffect(() => {
    setAuthErrorHandler((errorCode) => {
      // Clear user data and show login modal
      clearUser();
      openLogin();
    });
  }, [clearUser, openLogin]);

  // Load user info from localStorage on initialization
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      // Track visitor with stored user data
      trackVisitor({
        email: parsedUser.email,
        name: parsedUser.name,
        id: parsedUser.id
      });
    }
    setLoading(false);
  }, []);

  // Update user information
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Track visitor with new user data
    trackVisitor({
      email: userData.email,
      name: userData.name,
      id: userData.id
    });
  };

  // Close login modal
  const closeLogin = () => {
    setShowLogin(false);
  };

  const value = {
    user,
    loading,
    showLogin,
    updateUser,
    clearUser,
    openLogin,
    closeLogin
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// Custom hook for using context in components
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
} 